import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import toast from "react-hot-toast";
import api from "../utils/api";
import AdminLayout from "../components/AdminLayout";
import { Card, SearchBar, FilterSelect, Skeleton, PageHeader, IconButton, cx } from "../components/UI";
import { Navigation, MapPin, TriangleAlert } from "lucide-react";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const STATUS_STYLES = {
  active: { dot: "#10b981", label: "Active" },
  inactive: { dot: "#94a3b8", label: "Inactive" },
  completed: { dot: "#3b82f6", label: "Completed" },
  "on-hold": { dot: "#f59e0b", label: "On hold" },
};

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

function buildQuery({ year, month, date, status }) {
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  else if (year) {
    params.set("year", year);
    if (month) params.set("month", month);
  }
  if (status) params.set("status", status);
  return params.toString();
}

const openDirections = (site) => {
  const { lat, lng } = site.coordinates || {};
  if (typeof lat !== "number" || typeof lng !== "number") return;
  window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, "_blank", "noopener,noreferrer");
};

export default function AdminSitesMap() {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ year: "", month: "", date: "", status: "" });

  /* ── init map once ── */
  useEffect(() => {
    if (mapRef.current) return;
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/light-v11",
      center: [76.2711, 10.5276],
      zoom: 7,
    });
    mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-right");
    mapRef.current.addControl(new mapboxgl.GeolocateControl({ trackUserLocation: false }), "top-right");
    return () => { mapRef.current?.remove(); mapRef.current = null; };
  }, []);

  const fetchSites = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQuery(filters);
      const res = await api.get(`/sites/locations/map${qs ? `?${qs}` : ""}`);
      setSites(res?.data || []);
    } catch (err) {
      toast.error(err.message || "Could not load sites");
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchSites(); }, [fetchSites]);

  // The list narrows with the search box; the map keeps every loaded pin.
  const visibleSites = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sites;
    return sites.filter((s) =>
      [s.name, s.code, s.city, s.state].filter(Boolean).some((v) => v.toLowerCase().includes(q)));
  }, [sites, search]);

  /* ── redraw markers ── */
  useEffect(() => {
    if (!mapRef.current) return;
    markersRef.current.forEach((m) => m.marker.remove());
    markersRef.current = [];
    if (sites.length === 0) return;

    const bounds = new mapboxgl.LngLatBounds();

    sites.forEach((site) => {
      const { lat, lng } = site.coordinates || {};
      if (typeof lat !== "number" || typeof lng !== "number") return;
      const style = STATUS_STYLES[site.status] || STATUS_STYLES.active;

      const el = document.createElement("button");
      el.setAttribute("aria-label", `Open ${site.name}`);
      Object.assign(el.style, {
        width: "18px", height: "18px", borderRadius: "9999px", background: style.dot,
        border: "2px solid white", boxShadow: "0 1px 4px rgba(15,23,42,0.35)",
        cursor: "pointer", transition: "transform 120ms ease",
      });
      el.addEventListener("mouseenter", () => (el.style.transform = "scale(1.25)"));
      el.addEventListener("mouseleave", () => (el.style.transform = "scale(1)"));

      const popup = new mapboxgl.Popup({ offset: 16, closeButton: false }).setHTML(`
        <div style="font-family: 'DM Sans', ui-sans-serif, system-ui; min-width: 180px;">
          <div style="font-weight:600; font-size:13px; color:#0f172a;">${site.name}</div>
          <div style="font-size:12px; color:#64748b; margin-top:2px;">${site.code || ""}</div>
          <div style="font-size:12px; color:#64748b; margin-top:4px;">${[site.city, site.state].filter(Boolean).join(", ")}</div>
          <div style="font-size:11px; color:${style.dot}; margin-top:6px; font-weight:600;">${style.label}</div>
          <a href="https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}"
             target="_blank" rel="noopener noreferrer"
             style="display:inline-flex; align-items:center; gap:4px; margin-top:8px; font-size:12px; font-weight:600; color:#2563eb; text-decoration:none;">
            Directions →
          </a>
        </div>
      `);

      const marker = new mapboxgl.Marker({ element: el }).setLngLat([lng, lat]).setPopup(popup).addTo(mapRef.current);
      el.addEventListener("click", () => setSelectedSiteId(site._id));
      markersRef.current.push({ id: site._id, marker });
      bounds.extend([lng, lat]);
    });

    if (!bounds.isEmpty()) mapRef.current.fitBounds(bounds, { padding: 60, maxZoom: 13, duration: 600 });
  }, [sites]);

  /* ── fly to the site picked in the list ── */
  useEffect(() => {
    if (!selectedSiteId || !mapRef.current) return;
    const site = sites.find((s) => s._id === selectedSiteId);
    if (!site?.coordinates) return;
    mapRef.current.flyTo({ center: [site.coordinates.lng, site.coordinates.lat], zoom: 14, duration: 700 });
    const entry = markersRef.current.find((m) => m.id === selectedSiteId);
    if (entry && !entry.marker.getPopup().isOpen()) entry.marker.togglePopup();
  }, [selectedSiteId, sites]);

  const clearFilters = () => { setFilters({ year: "", month: "", date: "", status: "" }); setSearch(""); };
  const hasActiveFilters = filters.year || filters.month || filters.date || filters.status || search;
  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

  return (
    <AdminLayout>
      <div className="space-y-4">
        <PageHeader
          title="Site Map"
          subtitle={loading ? "Loading sites…" : `${visibleSites.length} of ${sites.length} site${sites.length === 1 ? "" : "s"} shown`}
        >
          {/* legend */}
          <div className="hidden md:flex items-center gap-3">
            {Object.entries(STATUS_STYLES).map(([value, { dot, label }]) => (
              <span key={value} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: dot }} />
                <span className="text-xs text-ink-500">{label}</span>
              </span>
            ))}
          </div>
        </PageHeader>

        {/* ── filters ── */}
        <Card className="p-3 flex flex-wrap items-center gap-2.5">
          <FilterSelect
            value={filters.year}
            onChange={(v) => setFilters((f) => ({ ...f, year: v, date: "" }))}
            ariaLabel="Filter by year" className="min-w-[120px]"
          >
            <option value="">All years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </FilterSelect>

          <div className={cx("min-w-[140px]", !filters.year && "opacity-50 pointer-events-none")}>
            <FilterSelect
              value={filters.month}
              onChange={(v) => setFilters((f) => ({ ...f, month: v, date: "" }))}
              ariaLabel="Filter by month"
            >
              <option value="">All months</option>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </FilterSelect>
          </div>

          <input
            type="date"
            value={filters.date}
            onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value, year: "", month: "" }))}
            aria-label="Filter by exact date"
            className="bg-white border border-ink-300 rounded-xl px-3.5 py-2.5 text-sm text-ink-700 hover:border-ink-400 focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all"
          />

          <FilterSelect
            value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            ariaLabel="Filter by status" className="min-w-[140px]"
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_STYLES).map(([value, { label }]) => <option key={value} value={value}>{label}</option>)}
          </FilterSelect>

          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-xs font-semibold text-brand-600 hover:text-brand-700 px-1 whitespace-nowrap">
              Clear filters
            </button>
          )}
        </Card>

        {/* ── map + list ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
          <Card className="overflow-hidden p-0">
            {/* dvh keeps the map usable when mobile browser chrome collapses */}
            <div ref={mapContainerRef} className="h-[48dvh] lg:h-[calc(100vh-19rem)] lg:min-h-[420px] w-full" />
          </Card>

          <Card className="flex flex-col overflow-hidden max-h-[52dvh] lg:max-h-[calc(100vh-19rem)]">
            <div className="p-3 border-b border-ink-100 shrink-0">
              <SearchBar value={search} onChange={setSearch} placeholder="Search sites…" />
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain">
              {loading ? (
                <div className="space-y-2 p-3">
                  {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}
                </div>
              ) : visibleSites.length === 0 ? (
                <div className="p-8 text-center">
                  <MapPin size={22} className="mx-auto text-ink-300 mb-2" />
                  <p className="text-sm text-ink-400">No sites match your filters.</p>
                </div>
              ) : (
                <ul className="divide-y divide-ink-100">
                  {visibleSites.map((site) => {
                    const style = STATUS_STYLES[site.status] || STATUS_STYLES.active;
                    const hasCoords = typeof site.coordinates?.lat === "number";
                    const isSelected = selectedSiteId === site._id;
                    return (
                      <li key={site._id}>
                        <div className={cx("group flex items-start gap-2 px-3 py-3 transition-colors",
                          isSelected ? "bg-brand-50" : "hover:bg-ink-50")}>
                          <button
                            onClick={() => hasCoords && setSelectedSiteId(site._id)}
                            disabled={!hasCoords}
                            className={cx("flex flex-1 flex-col items-start gap-0.5 text-left min-w-0", !hasCoords && "opacity-60")}
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: style.dot }} />
                              <span className={cx("text-sm font-medium truncate", isSelected ? "text-brand-700" : "text-ink-900")}>
                                {site.name}
                              </span>
                            </span>
                            <span className="pl-4 text-xs text-ink-500 truncate max-w-full">
                              {[site.code, [site.city, site.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ") || "—"}
                            </span>
                            {!hasCoords && (
                              <span className="pl-4 text-xs text-amber-600 flex items-center gap-1">
                                <TriangleAlert size={11} /> No location marked
                              </span>
                            )}
                          </button>

                          {hasCoords && (
                            <IconButton
                              size="sm" variant="ghost" icon={<Navigation size={15} />}
                              onClick={(e) => { e.stopPropagation(); openDirections(site); }}
                              title={`Directions to ${site.name}`}
                              className="opacity-100 lg:opacity-0 lg:group-hover:opacity-100 lg:focus-visible:opacity-100 hover:text-emerald-600 hover:bg-emerald-50"
                            />
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
